import { useEffect, useRef } from "react";

// Per-list pagination + scroll memory (H1). Filters already live in the URL;
// this covers the rest of "return to where I was": which page of results, and
// where the user had scrolled. Keyed by the list URL (path + search) and held
// in sessionStorage (session-scoped is the right lifetime for back-navigation).

const PREFIX = "liststate:";
type Snapshot<C> = { cursorStack: C[]; scrollTop: number };

/** The single app scroll container (Layout's <main id="app-scroll">). Body
 *  scroll is locked, so every page scrolls inside this element. */
function getScrollContainer(): HTMLElement | null {
  return document.getElementById("app-scroll");
}

function read<C>(key: string): Snapshot<C> | null {
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    return raw ? (JSON.parse(raw) as Snapshot<C>) : null;
  } catch {
    return null;
  }
}

function write<C>(key: string, snap: Snapshot<C>): void {
  try {
    sessionStorage.setItem(PREFIX + key, JSON.stringify(snap));
  } catch {
    /* sessionStorage full / unavailable — non-fatal */
  }
}

/**
 * Saved pagination cursor stack for a list URL, for the list's useState lazy
 * initialiser:
 *
 *   const [cursorStack, setCursorStack] = useState(
 *     () => readCursorSnapshot<Cursor>(listKey) ?? [undefined],
 *   );
 *
 * Restoring the cursor here (rather than inside the hook) avoids calling the
 * parent's setState during render. Returns null unless a real (beyond page 1)
 * stack was saved.
 */
export function readCursorSnapshot<C>(key: string): C[] | null {
  const snap = read<C>(key);
  return snap && Array.isArray(snap.cursorStack) && snap.cursorStack.length > 1
    ? snap.cursorStack
    : null;
}

/**
 * Snapshot a list's cursor stack + scroll position to sessionStorage on
 * unmount (i.e. when drilling into a detail page), and restore the scroll
 * position when the list remounts on return.
 *
 * `cursorStack` is optional: flat (non-paginated) lists call this with just a
 * `key` to get scroll restore only.
 */
export function useListStateRestore<C>({
  key,
  cursorStack = [],
}: {
  key: string;
  cursorStack?: C[];
}): void {
  // Stable container whose fields are kept current after each commit; the
  // effect cleanup reads the latest values from it at unmount.
  const latest = useRef<{ cursorStack: C[]; scrollTop: number }>({
    cursorStack,
    scrollTop: 0,
  });
  // Sync after commit, not during render. Declared before the effect below so
  // React runs it first within a commit, and — because cleanups run before any
  // effect body — a commit that changes `key` snapshots the *previous* render's
  // cursor stack under the *previous* key, which is what we want.
  useEffect(() => {
    latest.current.cursorStack = cursorStack;
  });

  useEffect(() => {
    const el = getScrollContainer();
    if (!el) return;
    const store = latest.current;

    const snap = read<C>(key);
    if (snap) {
      // Defer until rows paint so scrollHeight is large enough to scroll to.
      requestAnimationFrame(() => {
        const el2 = getScrollContainer();
        if (el2) el2.scrollTop = snap.scrollTop;
      });
    }

    const onScroll = () => {
      store.scrollTop = el.scrollTop;
    };
    el.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      el.removeEventListener("scroll", onScroll);
      write(key, { cursorStack: store.cursorStack, scrollTop: store.scrollTop });
    };
  }, [key]);
}
